import React, { type ReactNode } from 'react'

import { F0Box, F0Heading, F0Text } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'

export type MarketplaceProductGroup<T> = {
  id: string
  title: string
  description?: string
  items: T[]
}

type MarketplaceProductGridProps<T> = {
  groups: MarketplaceProductGroup<T>[]
  renderCard: (item: T) => ReactNode
  emptyState?: {
    title: string
    description: string
  }
  isLoading?: boolean
  showLoadingState?: boolean
}

export function MarketplaceProductGrid<T extends { id: string }>({
  groups,
  renderCard,
  emptyState,
  isLoading,
  showLoadingState = true,
}: MarketplaceProductGridProps<T>) {
  if (isLoading) {
    if (!showLoadingState) {
      return null
    }

    return (
      <F0Box display="flex" justifyContent="center" alignItems="center" padding="2xl">
        <Spinner />
      </F0Box>
    )
  }

  const nonEmptyGroups = groups.filter((g) => g.items.length > 0)

  if (nonEmptyGroups.length === 0 && emptyState) {
    return (
      <F0Box display="flex" flexDirection="column" alignItems="center" padding="2xl" gap="sm">
        <F0Text variant="label" content={emptyState.title} />
        <F0Text variant="description" content={emptyState.description} />
      </F0Box>
    )
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="3xl">
      {nonEmptyGroups.map((group, index) => (
        <F0Box key={group.id} display="flex" flexDirection="column">
          <F0Box
            display="flex"
            flexDirection="column"
            gap="sm"
            paddingTop={index > 0 ? '2xl' : undefined}
            paddingBottom="lg"
            paddingLeft="sm"
          >
            <F0Heading variant="heading" content={group.title} />
            {group.description && <F0Text variant="description" content={group.description} />}
          </F0Box>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {group.items.map((item) => renderCard(item))}
          </div>
        </F0Box>
      ))}
    </F0Box>
  )
}
